import React from "react";

import { formatDate } from "~/utils/heartRateFormatters";
import { HealthBreakdown } from "../Shared";

interface HeartRateData {
  date: string;
  low: number;
  high: number;
  avg: number;
}

interface HourlyHeartRateData {
  datetime: string;
  hour: number;
  low: number;
  high: number;
  avg: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface HeartRateBreakdownProps {
  selectedRange: TimeRange;
  filteredData: (HeartRateData | HourlyHeartRateData)[];
  breakdownTitle: string;
  loading: boolean;
  error: string | null;
}

export const HeartRateBreakdown: React.FC<HeartRateBreakdownProps> = ({
  selectedRange,
  filteredData,
  breakdownTitle,
  loading,
  error,
}) => {
  const getKey = (item: HeartRateData | HourlyHeartRateData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyHeartRateData).datetime
      : (item as HeartRateData).date;
  };

  const getTimeDisplay = (item: HeartRateData | HourlyHeartRateData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyHeartRateData).hour + ":00"
      : formatDate((item as HeartRateData).date, selectedRange);
  };

  const getValues = (item: HeartRateData | HourlyHeartRateData) => [
    {
      label: "Low",
      value: item.low,
      color: "#3b82f6", // blue-500
      unit: "bpm",
    },
    {
      label: "Avg",
      value: item.avg,
      color: "#22c55e", // green-500
      unit: "bpm",
    },
    {
      label: "High",
      value: item.high,
      color: "#ef4444", // red-500
      unit: "bpm",
    },
  ];

  return (
    <HealthBreakdown
      selectedRange={selectedRange}
      filteredData={filteredData}
      breakdownTitle={breakdownTitle}
      loading={loading}
      error={error}
      dataTypeLabel="Heart Rate Data"
      getKey={getKey}
      getTimeDisplay={getTimeDisplay}
      getValues={getValues}
      noDataMessage="No heart rate data available"
    />
  );
};
